import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ActionsCell,
  Badge,
  ClickableRow,
  EmptyState,
  IconButton,
  Page,
  PageHeader,
  Pagination,
  SearchInput,
  TableSkeleton,
  usePagination,
} from "../../../components/ui";
import { RangePicker } from "../../../components/detail";
import { getBroadcastList } from "../../../services/broadcasts";
import { getDate } from "../../../utils/format";
import { matches, newestFirst, stripHtml } from "../../../utils/list";
import "./broadcast.css";

const NO_RANGE = { startDate: "", endDate: "" };

/** Broadcasts whose title matches `search`, sent within `range` (days included). */
export function filterBroadcasts(broadcasts, { search, range }) {
  const start = range.startDate ? new Date(range.startDate) : null;
  const end = range.endDate ? new Date(range.endDate) : null;
  return (broadcasts || []).filter((broadcast) => {
    const sentOn = new Date(broadcast.createdAt);
    sentOn.setHours(0, 0, 0, 0);
    return (
      matches(broadcast.title, search) &&
      (!start || !end || (sentOn >= start && sentOn <= end))
    );
  });
}

const MyBroadcast = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [range, setRange] = useState(NO_RANGE);

  useEffect(() => {
    getBroadcastList().then((data) => {
      setBroadcasts(newestFirst(data) || []);
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(
    () => filterBroadcasts(broadcasts, { search, range }),
    [broadcasts, search, range]
  );
  const pagination = usePagination(filtered, 10, [search, range]);
  const infoPath = (broadcast) => `/dashboard/broadcast-info/${broadcast._id}`;

  return (
    <Page>
      <PageHeader
        title={t("My Broadcasts")}
        subtitle={
          loading ? " " : t("broadcastCount", { count: broadcasts.length })
        }
        actions={
          <Link to="/dashboard/new-broadcast" className="ui-btn">
            <i className="ri-add-line" aria-hidden="true"></i>
            {t("New Broadcast")}
          </Link>
        }
      />

      <section className="ui-card">
        <div className="ui-toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t("Search Broadcast by Title . . .")}
          />
          <RangePicker range={range} onChange={setRange} emptyLabel={t("Sent")} />
        </div>

        {loading ? (
          <TableSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-mail-send-line">{t("No Data Found")}</EmptyState>
        ) : (
          <>
            <div className="ui-table-wrap">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th>{t("Title")}</th>
                    <th>{t("Message")}</th>
                    <th>{t("Recipients")}</th>
                    <th>{t("Date")}</th>
                    <th className="ui-cell-actions">
                      <span className="sr-only">{t("action")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagination.pageItems.map((broadcast) => {
                    const message = stripHtml(broadcast.message);
                    return (
                      <ClickableRow key={broadcast._id} to={infoPath(broadcast)}>
                        <td>
                          <span className="ui-media-heading">
                            <Link
                              to={infoPath(broadcast)}
                              className="ui-media-title"
                              title={broadcast.title}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {broadcast.title || "N/A"}
                            </Link>
                            {broadcast.type === "html" && (
                              <Badge color="blue">HTML</Badge>
                            )}
                          </span>
                          <span className="ui-media-meta">
                            {broadcast.subject || "N/A"}
                          </span>
                        </td>
                        <td>
                          <span
                            className="ui-clamp"
                            style={{ maxWidth: 360 }}
                            title={message}
                          >
                            {message || "N/A"}
                          </span>
                        </td>
                        <td className="ui-cell-muted">
                          {(broadcast.users || []).length}
                        </td>
                        <td className="ui-cell-muted">
                          {broadcast.createdAt ? getDate(broadcast.createdAt) : "N/A"}
                        </td>
                        <ActionsCell>
                          <IconButton
                            icon="ri-eye-line"
                            label={t("View")}
                            onClick={() => history.push(infoPath(broadcast))}
                          />
                        </ActionsCell>
                      </ClickableRow>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination {...pagination} onChange={pagination.setPage} />
          </>
        )}
      </section>
    </Page>
  );
};

export default MyBroadcast;
